import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  LinearProgress,
  MenuItem,
  Modal,
  Select,
  Snackbar,
  Typography,
} from "@mui/material";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { continueTrainInfoState, heatmapModelState, myAIModuleState, trainUuidState } from "../../recoil/upgrade";
import { useParams } from "react-router-dom";
import { InfoItem, InspModelItem } from "@types";
import { Play } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { getInspModel } from "../../services/modelService";
import { fetchProjectInspModels } from "../../services/inspModelCache";
import { ensureAIServer } from "../../services/aiServer";
import {
  continueTrainState,
  openDefectPreviewState,
  parentModelState,
  selectedInspModelState,
  thresholdCustomState,
} from "../../recoil";
import { modelThreshold, useParsedModels } from "../../hooks/useParsedModels";
import { t } from "i18next";

interface HistoryMapItem {
  trainHistory_id: number;
  label: string;
}

interface EpochListItem {
  id: number;
  uuid: string;
  epoch: number;
  trainHistory_id: number;
  validHistory_id: number | null;
  historyIndex: number;
  overkillPercent: string;
}

interface SelectionInspModelItem {
  historyMap: HistoryMapItem[];
  epochList: EpochListItem[];
  inspModelList?: InspModelItem[]; // 부모가 넘기면 부모 목록 사용 (저장 시 즉시 반영)
  thresholdCustom?: number;
  setThresholdCustom?: (th: number) => void;
  hideActions?: boolean; // 모델 선택만 필요한 곳(스냅샷 탭)에서 이어학습/부모모델 버튼 숨김
}

const selectSx = {
  borderRadius: "8px",
  backgroundColor: "var(--dk-surface, #fff)",
  minHeight: 40,
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    py: 0,
    px: 1.25,
    minHeight: "40px !important",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "0.5px solid var(--dk-border, rgba(0,0,0,0.12))",
    transition: "border-color 0.15s",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "0.5px solid var(--dk-border, rgba(0,0,0,0.25))",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "1px solid rgba(59,130,246,0.6)",
  },
};

const menuPaperSx = {
  maxHeight: "300px",
  overflowY: "auto",
  borderRadius: "12px",
  boxShadow: "0 12px 32px -4px rgba(0,0,0,0.12), 0 4px 12px rgba(0,0,0,0.06), 0 0 0 0.5px rgba(0,0,0,0.06)",
  mt: 0.75,
  "& .MuiList-root": { py: 0.75, px: 0.5 },
  "& .MuiMenuItem-root": {
    fontSize: 12.5,
    borderRadius: "6px",
    minHeight: "unset",
    py: 0.65,
    px: 1.25,
    "&.Mui-selected": {
      backgroundColor: "rgba(59,130,246,0.08)",
      color: "#2563eb",
      fontWeight: 600,
      "&:hover": { backgroundColor: "rgba(59,130,246,0.12)" },
    },
    "&:hover": { backgroundColor: "rgba(0,0,0,0.04)" },
    "&.Mui-disabled": {
      opacity: 1,
      color: "var(--dk-text-3, #9ca3af)",
      fontSize: 11,
      py: 0.4,
      letterSpacing: "0.02em",
    },
  },
};

export default function SelectionInspModel({
  historyMap,
  epochList,
  inspModelList: inspModelListProp,
  thresholdCustom,
  setThresholdCustom: setThresholdCustomProp,
  hideActions,
}: SelectionInspModelItem) {
  const setThresholdAtom = useSetRecoilState(thresholdCustomState);
  const setThresholdCustom = setThresholdCustomProp ?? setThresholdAtom;
  const { id } = useParams();
  const [ownInspModelList, setInspModelList] = useState<InspModelItem[]>([]);
  const inspModelList = inspModelListProp ?? ownInspModelList;
  const [continueTrainInfo, setContinueTrainInfo] = useRecoilState(continueTrainInfoState);
  const [selectedInspModel, setSelectedInspModel] = useRecoilState(selectedInspModelState);
  const [openDefectPreview, setOpenDefectPreview] = useRecoilState(openDefectPreviewState);
  const [parentModel, setParentModel] = useRecoilState(parentModelState);
  const [continueTrain, setContinueTrain] = useRecoilState(continueTrainState);
  const [myAIModule, setMyAIModule] = useRecoilState(myAIModuleState);
  const setHeatmapModel = useSetRecoilState(heatmapModelState);
  const trainUuid = useRecoilValue(trainUuidState);
  const pickHeatmapModel = (model: { id: number; uuid: string; epoch: number; historyIndex?: number }) =>
    setHeatmapModel({
      uuid: model.uuid,
      label: `${model.historyIndex ? `${model.historyIndex}${t("Round")} ` : ""}${t("Epoch")} ${model.epoch}`,
      source: "insp",
      modelId: model.id,
    });

  const parsedModels = useParsedModels(inspModelList);

  const [openMoveToast, setOpenMoveToast] = useState(false);
  const moveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [countdown, setCountdown] = useState(3);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);

  const [info, setInfo] = useState<InfoItem>({
    performance: {
      overkill: 0,
      missing: 0,
      threshold: 0.0,
    },
  });

  useEffect(() => {
    if (!inspModelListProp) fetchInspModel();
  }, []);

  const fetchInspModel = async () => {
    try {
      setInspModelList(await fetchProjectInspModels(Number(id)));
    } catch (error: any) {
      console.error(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      if (continueTrainInfo.trainHistory_id && continueTrainInfo.epoch) {
        const found = parsedModels.find(
          (m) =>
            m.trainHistory_id === continueTrainInfo.trainHistory_id &&
            m.epoch?.current === continueTrainInfo.epoch.current,
        );

        if (found) {
          const info = found.info ? JSON.parse(found.info) : null;
          if (info) setInfo(info);

          if (selectedInspModel?.id !== found.id) {
            setSelectedInspModel(found);
            const th = modelThreshold(info);
            if (th !== null) setThresholdCustom?.(th);
          }
        }
      }
    };
    fetchData();
  }, [parsedModels]);

  useEffect(() => {
    if (!selectedInspModel) return;
    const infoData = JSON.parse(selectedInspModel.info || "{}");
    setContinueTrainInfo({
      id: selectedInspModel.id,
      uuid: selectedInspModel.uuid,
      epoch: infoData.epoch || null,
      validHistory_id: infoData.validHistory_id || null,
      trainHistory_id: infoData.trainHistory_id || null,
    });
  }, [selectedInspModel]);

  const handleParentModel = (parentId?: number) => {
    if (!parentId) return;

    setCountdown(3);
    setOpenMoveToast(true);

    // 1초마다 숫자 감소
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => prev - 1);
    }, 1000);

    moveTimerRef.current = setTimeout(async () => {
      const response = await getInspModel(parentId);
      console.log("response", response.info);
      pickHeatmapModel({
        id: parentId,
        uuid: response.uuid,
        epoch: response.info?.epoch?.current ?? response.info?.epoch,
      });
      setContinueTrainInfo({
        id: parentId,
        uuid: response.uuid,
        trainHistory_id: response.info.trainHistory_id,
        validHistory_id: response.info.validHistory_id,
        epoch: response.info.epoch || continueTrainInfo.epoch,
      });
      if (setThresholdCustom) {
        setThresholdCustom(response.info.threshold ?? thresholdCustom ?? 0);
      }
      setOpenMoveToast(false);
      clearInterval(countdownRef.current!);
    }, 3000);
  };

  const handleContinueTraining = async () => {
    if (!(await ensureAIServer(trainUuid))) return;

    setContinueTrain(true);
    setOpenDefectPreview(true);

    if (selectedInspModel && myAIModule && id) {
      setParentModel({
        id: selectedInspModel.id,
        uuid: selectedInspModel.uuid,
      });
    }
  };

  const cancelMove = () => {
    if (moveTimerRef.current) clearTimeout(moveTimerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);

    setOpenMoveToast(false);
  };

  return (
    <>
      <Snackbar open={openMoveToast} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          severity="info"
          sx={{ width: "100%", position: "relative" }}
          action={
            <Button color="inherit" size="small" onClick={cancelMove}>
              {t("Cancel")}
            </Button>
          }
        >
          {countdown}
          {t("SecondsUntilMoveMsg")}
          <LinearProgress
            variant="determinate"
            value={(countdown / 3) * 100}
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "100%",
              height: 4,
            }}
          />
        </Alert>
      </Snackbar>
      <Box sx={{ display: "flex", gap: 1, width: "100%", flexDirection: "column" }}>
        <Box sx={{ display: "flex", gap: 1, width: "100%", alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: "38%" }}>
            <Select
              value={
                historyMap.some((h) => h.trainHistory_id === continueTrainInfo.trainHistory_id)
                  ? continueTrainInfo.trainHistory_id
                  : ""
              }
              displayEmpty
              disabled={!historyMap.some((h) => h.trainHistory_id != null)}
              renderValue={(val) => (
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6 }}>
                  <Typography sx={{ fontSize: 10, color: "var(--dk-text-3, #9ca3af)", fontWeight: 500, lineHeight: 1 }}>
                    {t("Round")}
                  </Typography>
                  <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "var(--dk-text, #1f2937)", lineHeight: 1 }}>
                    {historyMap.find((h) => h.trainHistory_id === val)?.label ?? "—"}
                  </Typography>
                </Box>
              )}
              onChange={(e) => {
                const trainHistory_id = Number(e.target.value);
                const firstModel = epochList
                  .filter((ep) => ep.trainHistory_id === trainHistory_id)
                  .sort((a, b) => a.epoch - b.epoch)[0];
                if (!firstModel) return;

                pickHeatmapModel(firstModel);
                setContinueTrainInfo({
                  id: firstModel.id,
                  uuid: firstModel.uuid,
                  trainHistory_id,
                  validHistory_id: firstModel.validHistory_id,
                  epoch: { current: firstModel.epoch, total: -1, num_train: -1 },
                });
              }}
              sx={selectSx}
              MenuProps={{ PaperProps: { sx: menuPaperSx }, disableScrollLock: true }}
            >
              {historyMap
                .filter((h) => h.trainHistory_id != null)
                .map((h) => (
                  <MenuItem key={h.trainHistory_id} value={h.trainHistory_id}>
                    {h.label}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: 1 }}>
            <Select
              id="epoch-select"
              value={epochList.some((ep) => ep.id === continueTrainInfo.id) ? continueTrainInfo.id : ""}
              displayEmpty
              disabled={epochList.length === 0}
              renderValue={(val) => {
                const found = epochList.find((ep) => ep.id === val);
                return (
                  <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.6 }}>
                    <Typography
                      sx={{ fontSize: 10, color: "var(--dk-text-3, #9ca3af)", fontWeight: 500, lineHeight: 1 }}
                    >
                      {t("Epoch")}
                    </Typography>
                    <Typography
                      sx={{ fontSize: 12.5, fontWeight: 700, color: "var(--dk-text, #1f2937)", lineHeight: 1 }}
                    >
                      {found ? found.epoch : "—"}
                    </Typography>
                    {found && (
                      <Typography sx={{ fontSize: 10, color: "#f97316", fontWeight: 500, lineHeight: 1, ml: 0.25 }}>
                        {found.overkillPercent}
                      </Typography>
                    )}
                  </Box>
                );
              }}
              onChange={(e) => {
                const id = Number(e.target.value);
                const found = epochList.find((item) => item.id === id);
                if (!found) return;

                pickHeatmapModel(found);
                setContinueTrainInfo({
                  id: found.id,
                  uuid: found.uuid,
                  trainHistory_id: found.trainHistory_id,
                  validHistory_id: found.validHistory_id,
                  epoch: { current: found.epoch, total: -1, num_train: -1 },
                });
              }}
              sx={selectSx}
              MenuProps={{ PaperProps: { sx: menuPaperSx }, disableScrollLock: true }}
            >
              {[...epochList]
                .sort((a, b) => a.trainHistory_id - b.trainHistory_id || a.epoch - b.epoch)
                .map((item, index, sorted) => {
                  const prev = sorted[index - 1];
                  const isNewHistory = !prev || prev.trainHistory_id !== item.trainHistory_id;

                  return [
                    isNewHistory && (
                      <MenuItem key={`header-${item.trainHistory_id}`} disabled sx={{ fontSize: 12, opacity: 0.6 }}>
                        ── {item.historyIndex}
                        {t("Round")} ──
                      </MenuItem>
                    ),
                    <MenuItem key={item.id} value={item.id}>
                      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                        <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: "inherit" }}>
                          {t("Epoch")} {item.epoch}
                        </Typography>
                        <Typography sx={{ fontSize: 10.5, color: "#f97316", fontWeight: 500 }}>
                          {item.overkillPercent}
                        </Typography>
                      </Box>
                    </MenuItem>,
                  ];
                })}
            </Select>
          </FormControl>
        </Box>
        {!hideActions && (
          <Box sx={{ display: "flex", width: "100%", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              {info.parentModel && !openDefectPreview && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Button
                    onClick={() => handleParentModel(info.parentModel?.id)}
                    variant="outlined"
                    startIcon={<span style={{ fontSize: 12 }}>↩</span>}
                    sx={{
                      px: 1.25,
                      py: 0.5,
                      minWidth: "auto",
                      borderRadius: "8px",
                      fontSize: "0.72rem",
                      fontWeight: 500,
                      textTransform: "none",
                      boxShadow: "none",
                      color: "var(--dk-text-2, #6b7280)",
                      borderColor: "var(--dk-border, rgba(0,0,0,0.15))",
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.04)",
                        borderColor: "var(--dk-border, rgba(0,0,0,0.25))",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {t("ParentModel")}
                  </Button>
                </Box>
              )}
            </Box>

            {!openDefectPreview && (
              <Button
                onClick={handleContinueTraining}
                disabled={!myAIModule}
                variant="outlined"
                startIcon={<Play size={11} />}
                sx={{
                  px: 1.25,
                  py: 0.5,
                  minWidth: "auto",
                  borderRadius: "8px",
                  fontSize: "0.72rem",
                  fontWeight: 500,
                  textTransform: "none",
                  boxShadow: "none",
                  color: "#2563eb",
                  borderColor: "rgba(59,130,246,0.35)",
                  backgroundColor: "rgba(59,130,246,0.06)",
                  "&:hover": {
                    backgroundColor: "rgba(59,130,246,0.12)",
                    borderColor: "rgba(59,130,246,0.5)",
                    boxShadow: "none",
                  },
                }}
              >
                {t("ContinueTrainingStart")}
              </Button>
            )}
          </Box>
        )}
      </Box>
    </>
  );
}
